import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ALL_EXAM_LOCALES,
  fetchFreeCourses,
  fetchExamLevels,
  registerForExam,
  IFreeCourse,
  IExamLevel
} from 'common/modules/ReactShared/data/api/freeCourses';
import type { tStudentExaminationsMeta } from 'common/modules/ReactShared/data/api/tests';
import { getAllTargetLanguages } from 'common/modules/ReactShared/data/api/languages/api';
import type { IProfileLanguage } from 'common/modules/ReactShared/data/api/languages/types';
import type { tSharedAppState } from '../../types';
import * as Template from '../../_pagination';
import userSharedSelectors from '../../user/selectors';
import { selectors } from './selectors';
import { IItem, tTestsTab, IFreeCoursesFilters, IFreeCoursesState } from './types';
import { FETCHERS } from './fetchers';

// 'failed' is kept apart from 'initial': a tab that could not be loaded must
// not look like a tab with nothing in it.
type tTabState = {
  items: Template.IItems<IItem> | null;
  pagination: Template.IPagination | null;
  params: Template.IParams | null;
  status: 'initial' | 'fetch_loading' | 'received' | 'failed';
  totalCount: number | null;
  meta: tStudentExaminationsMeta | null;
};

type tTestsState = Record<tTestsTab, tTabState> & {
  freeCourses: IFreeCoursesState;
};

const PER_PAGE = 25;

const filtersMatch = (a: IFreeCoursesFilters, b: IFreeCoursesFilters): boolean =>
  a.level === b.level && a.language === b.language && a.search === b.search;

const sortLevels = (levels: IExamLevel[]): IExamLevel[] =>
  [...levels].sort((a, b) => a.shortName.localeCompare(b.shortName));

const defaultLanguageCode = (
  state: tSharedAppState,
  languages: IProfileLanguage[]
): string => {
  const targetLanguage = userSharedSelectors.selectProfileCurrentTargetLanguage(state);
  if (!targetLanguage) return '';

  return languages.find(({ id }) => id === targetLanguage.languageId)?.code ?? '';
};

const createInitialTabState = (): tTabState => ({
  items: null,
  pagination: null,
  params: null,
  status: 'initial',
  totalCount: null,
  meta: null
});

const initialFreeCoursesFilters: IFreeCoursesFilters = {
  level: '',
  language: '',
  search: ''
};

const initialFreeCoursesState: IFreeCoursesState = {
  items: null,
  pagination: null,
  status: 'initial',
  totalCount: null,
  filters: initialFreeCoursesFilters,
  selectedId: null,
  languages: [],
  examLevels: []
};

const initialState: tTestsState = {
  in_progress: createInitialTabState(),
  pending: createInitialTabState(),
  completed: createInitialTabState(),
  freeCourses: initialFreeCoursesState
};

const getItems = createAsyncThunk<
  {
    tab: tTestsTab;
    items: Template.IItems<IItem>;
    pagination: Template.IPagination;
    params: Template.IParams | null;
    totalCount: number;
    meta: tStudentExaminationsMeta | null;
  },
  { tab: tTestsTab; params?: Template.IParams }
>('tests/getItems_request', async ({ tab, params }) => {
  const fetcher = FETCHERS[tab];
  const response = await fetcher({
    page: params?.page ?? 1,
    per_page: params?.perPage ?? PER_PAGE
  });

  const { records, totalCount, meta } = response;

  const currentPage: number = (params?.page as number) ?? 1;
  const itemsPerPage: number = (params?.perPage as number) ?? PER_PAGE;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return {
    tab,
    items: records,
    pagination: {
      currentPage,
      totalPages,
      nextPage: currentPage < totalPages ? currentPage + 1 : null,
      prevPage: currentPage > 1 ? currentPage - 1 : null,
      limitValue: itemsPerPage
    },
    params: params ?? null,
    totalCount,
    meta
  };
});

const getMoreItems = createAsyncThunk<
  {
    tab: tTestsTab;
    succeeded: boolean;
    nextItems?: Template.IItems<IItem>;
    newPagination?: Template.IPagination;
  },
  tTestsTab,
  { state: tSharedAppState }
>('tests/getMoreItems_request', async (tab, { getState }) => {
  const store = selectors.selectStore(getState());
  const tabState = store[tab];
  const nextPage = tabState.pagination?.nextPage;

  if (!nextPage) return { tab, succeeded: false };

  const fetcher = FETCHERS[tab];
  const response = await fetcher({
    page: nextPage,
    per_page: PER_PAGE
  });

  const { records, totalCount } = response;

  const currentPage = nextPage;
  const totalPages = Math.ceil(totalCount / PER_PAGE);

  return {
    tab,
    succeeded: true,
    nextItems: records,
    newPagination: {
      currentPage,
      totalPages,
      nextPage: currentPage < totalPages ? currentPage + 1 : null,
      prevPage: currentPage > 1 ? currentPage - 1 : null,
      limitValue: PER_PAGE
    }
  };
});

const getFreeCourses = createAsyncThunk<
  {
    items: Template.IItems<IFreeCourse>;
    pagination: Template.IPagination;
    totalCount: number;
    filtersSnapshot: IFreeCoursesFilters;
  },
  void,
  { state: tSharedAppState }
>('tests/getFreeCourses_request', async (_, { getState }) => {
  const store = selectors.selectStore(getState());
  const filtersSnapshot = store.freeCourses.filters;

  const response = await fetchFreeCourses({
    page: 1,
    per_page: PER_PAGE,
    level_ids: filtersSnapshot.level ? [Number(filtersSnapshot.level)] : undefined,
    exam_locale: filtersSnapshot.language || ALL_EXAM_LOCALES,
    query: filtersSnapshot.search || undefined
  });

  const { records, totalCount, totalPages, currentPage } = response;

  return {
    items: records,
    pagination: {
      currentPage,
      totalPages,
      nextPage: currentPage < totalPages ? currentPage + 1 : null,
      prevPage: null
    },
    totalCount,
    filtersSnapshot
  };
});

const getMoreFreeCourses = createAsyncThunk<
  {
    succeeded: boolean;
    nextItems?: Template.IItems<IFreeCourse>;
    newPagination?: Template.IPagination;
    filtersSnapshot?: IFreeCoursesFilters;
  },
  void,
  { state: tSharedAppState }
>(
  'tests/getMoreFreeCourses_request',
  async (_, { getState }) => {
    const store = selectors.selectStore(getState());
    const nextPage = store.freeCourses.pagination?.nextPage;

    if (!nextPage) return { succeeded: false };

    const filtersSnapshot = store.freeCourses.filters;

    const response = await fetchFreeCourses({
      page: nextPage,
      per_page: PER_PAGE,
      level_ids: filtersSnapshot.level ? [Number(filtersSnapshot.level)] : undefined,
      exam_locale: filtersSnapshot.language || ALL_EXAM_LOCALES,
      query: filtersSnapshot.search || undefined
    });

    const { records, totalPages, currentPage } = response;

    return {
      succeeded: true,
      nextItems: records,
      newPagination: {
        currentPage,
        totalPages,
        nextPage: currentPage < totalPages ? currentPage + 1 : null,
        prevPage: currentPage > 1 ? currentPage - 1 : null
      },
      filtersSnapshot
    };
  },
  {
    condition: (_, { getState }) => {
      const store = selectors.selectStore(getState());
      return store.freeCourses.status !== 'fetch_loading';
    }
  }
);

const getFilterOptions = createAsyncThunk<{
  languages: IProfileLanguage[];
  examLevels: IExamLevel[];
}>('tests/getFilterOptions_request', async () => {
  const [languages, examLevels] = await Promise.all([
    getAllTargetLanguages(),
    fetchExamLevels()
  ]);
  return { languages, examLevels: sortLevels(examLevels) };
});

// Loads the filter options first so the language filter can be defaulted to the
// student's active target language before the list is fetched — one request, no
// flash of an unfiltered list.
const initFreeCourses = createAsyncThunk<void, void, { state: tSharedAppState }>(
  'tests/initFreeCourses_request',
  async (_, { dispatch, getState }) => {
    await dispatch(getFilterOptions());

    const state = getState();
    const { filters, languages } = selectors.selectStore(state).freeCourses;

    if (!filters.language) {
      const language = defaultLanguageCode(state, languages);
      if (language) dispatch(slice.actions.setFreeCoursesFilters({ language }));
    }

    await dispatch(getFreeCourses());
  }
);

const registerExam = createAsyncThunk<{ examId: number; url: string }, number>(
  'tests/registerExam_request',
  async (examId, { dispatch }) => {
    const url = await registerForExam(examId);
    dispatch(getItems({ tab: 'pending' }));
    return { examId, url };
  }
);

const slice = createSlice({
  name: 'tests',
  initialState,
  reducers: {
    setFreeCoursesFilters(state, action: PayloadAction<Partial<IFreeCoursesFilters>>) {
      state.freeCourses.filters = { ...state.freeCourses.filters, ...action.payload };
    },
    setFreeCoursesSelectedId(state, action: PayloadAction<number | null>) {
      state.freeCourses.selectedId = action.payload;
    },
    softResetFreeCourses(state) {
      state.freeCourses = {
        ...initialFreeCoursesState,
        // The language default is resolved once per session; keep it so the page
        // list behind the modal stays scoped after the modal unmounts.
        filters: {
          ...initialFreeCoursesFilters,
          language: state.freeCourses.filters.language
        },
        totalCount: state.freeCourses.totalCount,
        languages: state.freeCourses.languages,
        examLevels: state.freeCourses.examLevels
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getItems.pending, (state, action) => {
      const { tab } = action.meta.arg;
      state[tab].status = 'fetch_loading';
    });

    builder.addCase(getItems.fulfilled, (state, action) => {
      const { tab, items, pagination, params, totalCount, meta } = action.payload;
      state[tab].items = items;
      state[tab].pagination = pagination;
      state[tab].params = params;
      state[tab].totalCount = totalCount;
      state[tab].meta = meta;
      state[tab].status = 'received';
    });

    builder.addCase(getItems.rejected, (state, action) => {
      const { tab } = action.meta.arg;
      state[tab].status = 'failed';
    });

    builder.addCase(getMoreItems.fulfilled, (state, action) => {
      const { tab, succeeded, nextItems, newPagination } = action.payload;
      if (!succeeded || !newPagination) return;

      const existing = state[tab].items ?? [];
      state[tab].items = existing.concat(nextItems ?? []);
      state[tab].pagination = newPagination;
    });

    builder.addCase(getFreeCourses.pending, (state) => {
      state.freeCourses.status = 'fetch_loading';
    });

    builder.addCase(getFreeCourses.fulfilled, (state, action) => {
      const { items, pagination, totalCount, filtersSnapshot } = action.payload;
      if (!filtersMatch(filtersSnapshot, state.freeCourses.filters)) return;

      state.freeCourses.items = items;
      state.freeCourses.pagination = pagination;
      state.freeCourses.status = 'received';
      if (state.freeCourses.totalCount === null) {
        state.freeCourses.totalCount = totalCount;
      }
    });

    builder.addCase(getFreeCourses.rejected, (state) => {
      state.freeCourses.status = 'initial';
    });

    builder.addCase(getMoreFreeCourses.fulfilled, (state, action) => {
      const { succeeded, nextItems, newPagination, filtersSnapshot } = action.payload;
      if (!succeeded || !newPagination || !filtersSnapshot) return;
      if (!filtersMatch(filtersSnapshot, state.freeCourses.filters)) return;

      const existing = state.freeCourses.items ?? [];
      state.freeCourses.items = existing.concat(nextItems ?? []);
      state.freeCourses.pagination = newPagination;
    });

    builder.addCase(getFilterOptions.fulfilled, (state, action) => {
      state.freeCourses.languages = action.payload.languages;
      state.freeCourses.examLevels = action.payload.examLevels;
    });
  }
});

const { reducer } = slice;

const actions = {
  getItems,
  getMoreItems,
  initFreeCourses,
  getFreeCourses,
  getMoreFreeCourses,
  getFilterOptions,
  registerExam,
  ...slice.actions
};

export { reducer, actions };
export type { tTestsState, tTabState, tTestsTab };
